import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config';
import {
  FaSave,
  FaPlus,
  FaEdit,
  FaTrash,
  FaCogs,
  FaSmile,
  FaThLarge,
  FaLayerGroup,
  FaSearch,
  FaImage,
  FaUpload,
  FaSpinner,
  FaTimes,
  FaCheckCircle,
  FaExclamationCircle,
  FaBuilding,
} from 'react-icons/fa';
import EmojiPicker from 'emoji-picker-react';
import { useAdmin } from '../context/AdminContext';
import './SectionManager.css';

const DESC_LIMIT = 1000;
const CARD_DESC_LIMIT = 1500;

const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    if (file.size <= 500 * 1024) {
      resolve(file);
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas is empty'));
              return;
            }
            resolve(
              new File([blob], file.name.replace(/\.[^/.]+$/, '') + '.jpg', {
                type: 'image/jpeg',
                lastModified: Date.now(),
              })
            );
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const EMPTY_SERVICE = {
  title: '',
  description: '',
  icon: '',
  color: '#c9a227',
};

const ServicesManager = () => {
  const { data, addItem, updateItem, deleteItem } = useAdmin();

  const [mainTab, setMainTab] = useState('services');

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [form, setForm] = useState(EMPTY_SERVICE);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const servicesList = data?.services || [];
  const descLen = (form.description || '').length;

  const DYNAMIC_VERTICALS =
    servicesList.length > 0
      ? servicesList.map((s) => ({
          value: (s.title || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/(^_|_$)/g, ''),
          label: s.title || 'Untitled',
        }))
      : [{ value: 'education', label: 'Education' }];

  const [cardVertical, setCardVertical] = useState(DYNAMIC_VERTICALS[0].value);
  const [cardSearch, setCardSearch] = useState('');
  const [showCardForm, setShowCardForm] = useState(false);
  const [cardEditId, setCardEditId] = useState(null);

  const getEmptyCard = (vert) => ({
    vertical: vert,
    badge: vert.toUpperCase().replace(/_/g, ' '),
    title: '',
    full_desc: '',
    imageFile: null,
    imagePreview: '',
    existingImage: '',
  });

  const [cardForm, setCardForm] = useState(getEmptyCard(cardVertical));
  const [cardError, setCardError] = useState('');
  const [cardMsg, setCardMsg] = useState({ type: '', text: '' });
  const [cardsList, setCardsList] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [sizeInfo, setSizeInfo] = useState({ original: '', compressed: '' });

  const cardDescLen = (cardForm.full_desc || '').length;

  const getAuthToken = () => {
    return (
      localStorage.getItem('access_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('access') ||
      ''
    );
  };

  const fetchBackendCards = async (v = cardVertical) => {
    if (!v) return;
    setLoadingCards(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_BASE_URL}/service-projects/?vertical=${v}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const results = res.data?.results || res.data || [];
      setCardsList(results);
    } catch (err) {
      console.error('Failed to fetch cards:', err);
    } finally {
      setLoadingCards(false);
    }
  };

  useEffect(() => {
    if (mainTab === 'cards') {
      fetchBackendCards(cardVertical);
    }
  }, [cardVertical, mainTab]);

  const notifyCard = (type, text) => {
    setCardMsg({ type, text });
    setTimeout(() => setCardMsg({ type: '', text: '' }), 3000);
  };

  const notifySuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (descLen > DESC_LIMIT) {
      setError(`Description cannot exceed ${DESC_LIMIT} characters.`);
      return;
    }

    try {
      if (editId) {
        await updateItem('services', editId, form);
        notifySuccess('Business updated successfully!');
      } else {
        await addItem('services', form);
        notifySuccess('New business added successfully!');
      }
      resetForm();
    } catch (err) {
      console.error('Service save error:', err);
      setError('Failed to save. Please try again.');
    }
  };

  const handleEdit = (item) => {
    setForm({
      title: item.title || '',
      description: (item.description || '').slice(0, DESC_LIMIT),
      icon: item.icon || '',
      color: item.color || '#c9a227',
    });
    setEditId(item.id);
    setShowForm(true);
    setShowEmojiPicker(false);
    setError('');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this business vertical?')) {
      await deleteItem('services', id);
      notifySuccess('Business deleted successfully!');
    }
  };

  const resetForm = () => {
    setForm(EMPTY_SERVICE);
    setEditId(null);
    setShowForm(false);
    setShowEmojiPicker(false);
    setError('');
  };

  const handleEmojiSelect = (emojiData) => {
    setForm({ ...form, icon: emojiData.emoji });
    setShowEmojiPicker(false);
  };

  const resetCardForm = () => {
    setCardForm(getEmptyCard(cardVertical));
    setCardEditId(null);
    setShowCardForm(false);
    setSizeInfo({ original: '', compressed: '' });
    setCardError('');
  };

  const openAddCard = () => {
    setCardForm(getEmptyCard(cardVertical));
    setCardEditId(null);
    setShowCardForm(true);
    setSizeInfo({ original: '', compressed: '' });
    setCardError('');
  };

  const openEditCard = (item) => {
    const existingImg = item.image_display || item.image || item.image_url || '';

    setCardForm({
      vertical: item.vertical || cardVertical,
      badge: item.badge || (item.vertical || cardVertical).toUpperCase().replace(/_/g, ' '),
      title: item.title || '',
      full_desc: item.full_desc || item.short_desc || '',
      imageFile: null,
      imagePreview: existingImg,
      existingImage: existingImg,
    });
    setSizeInfo({ original: '', compressed: '' });
    setCardEditId(item.id);
    setShowCardForm(true);
    setCardError('');
  };

  const handleCardChange = (e) => {
    const { name, value } = e.target;
    if (name === 'full_desc' && value.length > CARD_DESC_LIMIT) return;
    setCardForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setCardError('Please upload a valid image file (jpg, png, webp).');
      return;
    }

    try {
      setIsCompressing(true);
      const originalSizeFormatted = formatFileSize(file.size);
      const compressed = await compressImage(file);
      const compressedSizeFormatted = formatFileSize(compressed.size);
      const previewUrl = URL.createObjectURL(compressed);
      setCardForm((prev) => ({
        ...prev,
        imageFile: compressed,
        imagePreview: previewUrl,
      }));
      setSizeInfo({
        original: originalSizeFormatted,
        compressed: compressedSizeFormatted,
      });
      setCardError('');
    } catch (err) {
      console.error('Compression Error:', err);
      setCardForm((prev) => ({
        ...prev,
        imageFile: file,
        imagePreview: URL.createObjectURL(file),
      }));
    } finally {
      setIsCompressing(false);
    }
  };

  const removeSelectedImage = () => {
    setCardForm((prev) => ({
      ...prev,
      imageFile: null,
      imagePreview: prev.existingImage || '',
    }));
    setSizeInfo({ original: '', compressed: '' });
  };

  const handleCardSubmit = async (e) => {
    e.preventDefault();
    setCardError('');

    if (!cardForm.title.trim()) {
      setCardError('Service Name is required.');
      return;
    }

    if (!cardEditId && !cardForm.imageFile && !cardForm.existingImage) {
      setCardError('Please upload an image for the service.');
      return;
    }

    const autoShortDesc = cardForm.full_desc
      ? cardForm.full_desc.slice(0, 120)
      : cardForm.title;

    try {
      const fd = new FormData();
      fd.append('vertical', cardForm.vertical);
      fd.append('badge', cardForm.badge || cardForm.vertical.toUpperCase().replace(/_/g, ' '));
      fd.append('title', cardForm.title.trim());
      fd.append('client', 'Sarwa Group');
      fd.append('year', String(new Date().getFullYear()));
      fd.append('location', '');
      fd.append('duration', '');
      fd.append('category', '');
      fd.append('short_desc', autoShortDesc.trim());
      fd.append('full_desc', cardForm.full_desc.trim());
      fd.append('features', JSON.stringify([]));
      fd.append('results', JSON.stringify([]));
      fd.append('is_active', 'true');
      fd.append('sort_order', '0');

      if (cardForm.imageFile) {
        fd.append('image', cardForm.imageFile);
      }

      const token = getAuthToken();
      const headers = {
        'Content-Type': 'multipart/form-data',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      if (cardEditId) {
        await axios.patch(`${API_BASE_URL}/service-projects/${cardEditId}/`, fd, { headers });
        notifyCard('success', 'Service updated successfully!');
      } else {
        await axios.post(`${API_BASE_URL}/service-projects/`, fd, { headers });
        notifyCard('success', 'New service added successfully!');
      }

      setCardVertical(cardForm.vertical);
      resetCardForm();
      fetchBackendCards(cardForm.vertical);
    } catch (err) {
      console.error('Card save error:', err.response?.data || err);
      const apiErr = err.response?.data;
      let msg = 'Service could not be saved. Please verify backend API.';
      if (apiErr && typeof apiErr === 'object') {
        msg = Object.values(apiErr).flat().join(' ') || msg;
      }
      setCardError(msg);
    }
  };

  const handleCardDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API_BASE_URL}/service-projects/${id}/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      notifyCard('success', 'Service deleted successfully.');
      fetchBackendCards(cardVertical);
    } catch (err) {
      notifyCard('error', 'Delete failed.');
    }
  };

  const filteredCards = cardsList.filter((c) => {
    const q = cardSearch.toLowerCase();
    if (!q) return true;
    return (c.title || '').toLowerCase().includes(q);
  });

  return (
    <div className="sg-manager sm-services-manager">
      {/* ═══════════════════════════════════════════
          PAGE HEADER
         ═══════════════════════════════════════════ */}
      <div className="sg-page-header">
        <div className="sg-ph-left">
          <div className="sg-ph-icon">
            <FaCogs />
          </div>
          <div className="sg-ph-text">
            <h2>Our Businesses</h2>
            <p>Manage group companies, verticals, and their service showcases</p>
          </div>
        </div>
        <div className="sg-ph-right">
          {mainTab === 'services' ? (
            <button className="sg-btn-gold" onClick={() => setShowForm(!showForm)}>
              {showForm ? <><FaTimes /> Close Form</> : <><FaPlus /> Add Business</>}
            </button>
          ) : (
            <button className="sg-btn-gold" onClick={openAddCard}>
              <FaPlus /> Add Service
            </button>
          )}
        </div>
      </div>

      {successMsg && (
        <div className="sg-alert sg-alert-success">
          <FaCheckCircle /> {successMsg}
        </div>
      )}

      {/* MAIN TABS */}
      <div className="sg-tabs-wrap">
        <button
          type="button"
          onClick={() => setMainTab('services')}
          className={`sg-tab ${mainTab === 'services' ? 'active' : ''}`}
        >
          <FaLayerGroup />
          <span>Business Verticals</span>
          <span className="sg-tab-count">{servicesList.length}</span>
        </button>
        <button
          type="button"
          onClick={() => setMainTab('cards')}
          className={`sg-tab ${mainTab === 'cards' ? 'active' : ''}`}
        >
          <FaThLarge />
          <span>Division Services</span>
          <span className="sg-tab-count">{cardsList.length}</span>
        </button>
      </div>

      {/* TAB 1: BUSINESS VERTICALS */}
      {mainTab === 'services' && (
        <>
          {showForm && (
            <div className="sg-form-card">
              <div className="sg-form-header">
                <div>
                  <h3>{editId ? 'Edit Business' : 'Add New Business'}</h3>
                  <p>Enter details for the business vertical</p>
                </div>
                <button className="sg-form-close" onClick={resetForm}>
                  <FaTimes />
                </button>
              </div>

              {error && (
                <div className="sg-alert sg-alert-error">
                  <FaExclamationCircle /> {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="sg-form">
                <div className="sg-form-row-2">
                  <div className="sg-field">
                    <label>Business Title <span className="req">*</span></label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      maxLength={60}
                      placeholder="e.g. Sarwa Education"
                      required
                    />
                  </div>

                  <div className="sg-field" style={{ position: 'relative' }}>
                    <label>Icon (Emoji) <span className="req">*</span></label>
                    <div className="sg-input-group">
                      <input
                        type="text"
                        value={form.icon}
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        onChange={(e) => setForm({ ...form, icon: e.target.value })}
                        placeholder="Click to pick emoji 🎓"
                        style={{ cursor: 'pointer' }}
                        required
                      />
                      <button
                        type="button"
                        className="sg-input-btn"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      >
                        <FaSmile />
                      </button>
                    </div>

                    {showEmojiPicker && (
                      <div className="sg-emoji-picker-wrap">
                        <EmojiPicker onEmojiClick={handleEmojiSelect} height={350} width={320} />
                      </div>
                    )}
                  </div>
                </div>

                <div className="sg-field">
                  <label className="sg-label-flex">
                    <span>Description <span className="req">*</span></span>
                    <span className={`sg-char-count ${descLen >= DESC_LIMIT ? 'over' : ''}`}>
                      {descLen}/{DESC_LIMIT}
                    </span>
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => {
                      if (e.target.value.length <= DESC_LIMIT) {
                        setForm({ ...form, description: e.target.value });
                      }
                    }}
                    rows="4"
                    maxLength={DESC_LIMIT}
                    placeholder="Brief description of this business vertical..."
                    required
                  />
                </div>

                <div className="sg-field sg-color-field">
                  <label>Brand Color</label>
                  <div className="sg-color-picker">
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                    />
                    <span className="sg-color-value">{form.color}</span>
                  </div>
                </div>

                <div className="sg-form-actions">
                  <button type="submit" className="sg-btn-gold">
                    <FaSave /> {editId ? 'Update Business' : 'Save Business'}
                  </button>
                  <button type="button" className="sg-btn-outline" onClick={resetForm}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="sg-table-card">
            <div className="sg-table-header">
              <div>
                <h3>All Business Verticals</h3>
                <p>Manage all businesses under Sarwa Group</p>
              </div>
              <span className="sg-count-badge">{servicesList.length} Total</span>
            </div>

            <div className="sg-table-wrap">
              <table className="sg-table">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }}>Icon</th>
                    <th>Title</th>
                    <th>Description</th>
                    <th style={{ width: '100px' }}>Color</th>
                    <th style={{ width: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {servicesList.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="sg-empty">
                        <div className="sg-empty-inner">
                          <FaBuilding />
                          <h4>No businesses yet</h4>
                          <p>Click "Add Business" to create your first business vertical</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    servicesList.map((item) => (
                      <tr key={item.id}>
                        <td data-label="Icon">
                          <div className="sg-row-icon">{item.icon || '🏢'}</div>
                        </td>
                        <td data-label="Title">
                          <strong className="sg-row-title">{item.title}</strong>
                        </td>
                        <td data-label="Description">
                          <span className="sg-row-desc">
                            {(item.description || '').slice(0, 80)}
                            {(item.description || '').length > 80 ? '...' : ''}
                          </span>
                        </td>
                        <td data-label="Color">
                          <div className="sg-color-swatch-wrap">
                            <div className="sg-color-swatch" style={{ background: item.color || '#c9a227' }} />
                            <span>{item.color || '#c9a227'}</span>
                          </div>
                        </td>
                        <td data-label="Actions">
                          <div className="sg-actions">
                            <button className="sg-action-btn sg-edit" onClick={() => handleEdit(item)} title="Edit">
                              <FaEdit />
                            </button>
                            <button className="sg-action-btn sg-delete" onClick={() => handleDelete(item.id)} title="Delete">
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* TAB 2: DIVISION SERVICES */}
      {mainTab === 'cards' && (
        <>
          {cardMsg.text && (
            <div className={`sg-alert sg-alert-${cardMsg.type}`}>
              {cardMsg.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
              {cardMsg.text}
            </div>
          )}

          <div className="sg-filter-bar">
            <div className="sg-vertical-chips">
              {DYNAMIC_VERTICALS.map((v) => (
                <button
                  key={v.value}
                  type="button"
                  onClick={() => setCardVertical(v.value)}
                  className={`sg-chip ${cardVertical === v.value ? 'active' : ''}`}
                >
                  {v.label}
                </button>
              ))}
            </div>

            <div className="sg-search-box">
              <FaSearch />
              <input
                value={cardSearch}
                onChange={(e) => setCardSearch(e.target.value)}
                placeholder="Search services by name..."
              />
              {cardSearch && (
                <button onClick={() => setCardSearch('')}>
                  <FaTimes />
                </button>
              )}
            </div>
          </div>

          {showCardForm && (
            <div className="sg-form-card">
              <div className="sg-form-header">
                <div>
                  <h3>{cardEditId ? 'Edit Service' : 'Add New Service'}</h3>
                  <p>
                    Add service details for the{' '}
                    {DYNAMIC_VERTICALS.find((v) => v.value === cardForm.vertical)?.label || ''} division
                  </p>
                </div>
                <button className="sg-form-close" onClick={resetCardForm}>
                  <FaTimes />
                </button>
              </div>

              {cardError && (
                <div className="sg-alert sg-alert-error">
                  <FaExclamationCircle /> {cardError}
                </div>
              )}

              <form onSubmit={handleCardSubmit} className="sg-form">
                <div className="sg-form-row-2">
                  <div className="sg-field">
                    <label>Business Division <span className="req">*</span></label>
                    <select
                      name="vertical"
                      value={cardForm.vertical}
                      onChange={(e) =>
                        setCardForm((p) => ({
                          ...p,
                          vertical: e.target.value,
                          badge: e.target.value.toUpperCase().replace(/_/g, ' '),
                        }))
                      }
                    >
                      {DYNAMIC_VERTICALS.map((v) => (
                        <option key={v.value} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sg-field">
                    <label>Service Name <span className="req">*</span></label>
                    <input
                      name="title"
                      value={cardForm.title}
                      onChange={handleCardChange}
                      placeholder="e.g. Smart Campus Setup"
                      required
                    />
                  </div>
                </div>

                <div className="sg-form-row-2">
                  <div className="sg-field">
                    <label>
                      Service Image {!cardEditId && <span className="req">*</span>}
                    </label>
                    <div className={`sg-upload-box ${cardForm.imagePreview ? 'has-image' : ''}`}>
                      {cardForm.imagePreview ? (
                        <div className="sg-upload-preview">
                          <img
                            src={cardForm.imagePreview}
                            alt="Preview"
                            className="sg-preview-img"
                          />
                          <div className="sg-upload-actions">
                            <label className="sg-upload-change">
                              {isCompressing ? (
                                <><FaSpinner className="spin" /> Compressing...</>
                              ) : (
                                <><FaUpload /> Change Image</>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                style={{ display: 'none' }}
                                disabled={isCompressing}
                              />
                            </label>
                            {cardForm.imageFile && (
                              <button
                                type="button"
                                className="sg-upload-remove"
                                onClick={removeSelectedImage}
                                disabled={isCompressing}
                              >
                                <FaTimes /> Remove
                              </button>
                            )}
                            {sizeInfo.compressed && (
                              <div className="sg-size-info">
                                <FaCheckCircle /> {sizeInfo.original} → {sizeInfo.compressed}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <label className="sg-upload-empty">
                          {isCompressing ? (
                            <FaSpinner className="spin sg-upload-icon" />
                          ) : (
                            <FaImage className="sg-upload-icon" />
                          )}
                          <span className="sg-upload-title">
                            {isCompressing ? 'Compressing...' : 'Click to Upload Image'}
                          </span>
                          <span className="sg-upload-hint">
                            JPG, PNG, WEBP • Auto Compression
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            style={{ display: 'none' }}
                            disabled={isCompressing}
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  <div className="sg-field">
                    <label className="sg-label-flex">
                      <span>Service Description <span className="req">*</span></span>
                      <span className={`sg-char-count ${cardDescLen >= CARD_DESC_LIMIT ? 'over' : ''}`}>
                        {cardDescLen}/{CARD_DESC_LIMIT}
                      </span>
                    </label>
                    <textarea
                      name="full_desc"
                      value={cardForm.full_desc}
                      onChange={handleCardChange}
                      placeholder="Write a detailed description about this service. This will be displayed on the public website..."
                      rows="10"
                      style={{ minHeight: '220px' }}
                      required
                    />
                  </div>
                </div>

                <div className="sg-form-actions">
                  <button type="submit" className="sg-btn-gold" disabled={isCompressing}>
                    <FaSave /> {cardEditId ? 'Update Service' : 'Save Service'}
                  </button>
                  <button
                    type="button"
                    className="sg-btn-outline"
                    onClick={resetCardForm}
                    disabled={isCompressing}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="sg-table-card">
            <div className="sg-table-header">
              <div>
                <h3>
                  {DYNAMIC_VERTICALS.find((v) => v.value === cardVertical)?.label || 'Division'} Services
                </h3>
                <p>All services under this business division</p>
              </div>
              <span className="sg-count-badge">{filteredCards.length} Total</span>
            </div>

            <div className="sg-table-wrap">
              <table className="sg-table">
                <thead>
                  <tr>
                    <th style={{ width: '100px' }}>Image</th>
                    <th style={{ width: '220px' }}>Service Name</th>
                    <th>Description Preview</th>
                    <th style={{ width: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingCards ? (
                    <tr>
                      <td colSpan="4" className="sg-empty">
                        <div className="sg-empty-inner">
                          <FaSpinner className="spin" />
                          <h4>Loading services...</h4>
                        </div>
                      </td>
                    </tr>
                  ) : filteredCards.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="sg-empty">
                        <div className="sg-empty-inner">
                          <FaThLarge />
                          <h4>No services yet</h4>
                          <p>Click "Add Service" to create the first one</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredCards.map((item) => (
                      <tr key={item.id}>
                        <td data-label="Image">
                          {item.image_display || item.image ? (
                            <img
                              src={item.image_display || item.image}
                              alt={item.title}
                              className="sg-table-img"
                            />
                          ) : (
                            <div className="sg-table-img-placeholder">
                              <FaImage />
                            </div>
                          )}
                        </td>
                        <td data-label="Service Name">
                          <strong className="sg-row-title">{item.title}</strong>
                        </td>
                        <td data-label="Description">
                          <span className="sg-row-desc">
                            {(item.full_desc || item.short_desc || '').slice(0, 100)}
                            {(item.full_desc || item.short_desc || '').length > 100 ? '...' : ''}
                          </span>
                        </td>
                        <td data-label="Actions">
                          <div className="sg-actions">
                            <button className="sg-action-btn sg-edit" onClick={() => openEditCard(item)} title="Edit">
                              <FaEdit />
                            </button>
                            <button className="sg-action-btn sg-delete" onClick={() => handleCardDelete(item.id)} title="Delete">
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ServicesManager;